import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import {
  basePrimaryButton,
  baseSecondaryButton,
  baseTertiaryButton,
  baseTransparentButton,
  basePrimaryTransparentButton,
} from '../Button/BaseStyles';

export type THyperLinkVariant =
  | 'link'
  | 'button-primary'
  | 'button-primary-span'
  | 'button-primary-transparent'
  | 'button-secondary'
  | 'button-secondary-span'
  | 'button-tertiary'
  | 'button-transparent'
  | 'no-style';

export interface IHyperLinkProps {
  disabled?: boolean;
  href?: string;
  onClick?: (event: React.MouseEvent) => void;
  to?: string;
  className?: string;
  variant?: THyperLinkVariant;
  children: React.ReactNode | string;
  hasMinWidth?: boolean;
  size?: 'medium' | 'large';
  dataTestId?: string;
}

interface IHyperLinks {
  'button-primary': typeof StyledPrimaryButton;
  'button-primary-span': typeof StyledPrimaryButtonSpan;
  'button-primary-transparent': typeof StyledPrimaryTransparentButton;
  'button-secondary': typeof StyledSecondaryButton;
  'button-secondary-span': typeof StyledSecondaryButtonSpan;
  'button-tertiary': typeof StyledTertiaryButton;
  'button-transparent': typeof StyledTransparentButton;
  'no-style': typeof NoStyledButton;
  link: typeof StyledHyperlink;
}

const StyledHyperlink = styled.a<IHyperLinkProps>`
  color: var(--clr-link);
  cursor: pointer;
  line-height: 1.375;
  text-decoration: underline;
  transition: all 0.3s ease;

  &:hover {
    opacity: var(--opacity-link-hover);
  }
`;

const StyledButton = styled.a<IHyperLinkProps>`
  min-width: ${(props) => (props.hasMinWidth ? '250px' : 'unset')};
  height: ${(props) => (props.size === 'large' ? '43px' : 'unset')};
`;

const StyledPrimaryButton = styled(StyledButton)<IHyperLinkProps>`
  ${basePrimaryButton};

  ${(props) =>
    props.disabled &&
    'opacity: 0.5; cursor: not-allowed; &:hover {background-color:  var(--clr-primary-1);}'}
`;
const StyledPrimaryButtonSpan = styled.span<IHyperLinkProps>`
  ${basePrimaryButton};

  ${(props) =>
    props.disabled &&
    'opacity: 0.5; cursor: not-allowed; &:hover {background-color:  var(--clr-primary-1);}'}
`;

const StyledSecondaryButton = styled(StyledButton)<IHyperLinkProps>`
  ${baseSecondaryButton};

  ${(props) =>
    props.disabled &&
    'opacity: 0.5; cursor: not-allowed; &:hover {background-color:  var(--black); color: var(--clr-primary-1);}'}
`;
const StyledSecondaryButtonSpan = styled.span<IHyperLinkProps>`
  ${baseSecondaryButton};

  ${(props) =>
    props.disabled &&
    'opacity: 0.5; cursor: not-allowed; &:hover {background-color:  var(--black); color: var(--clr-primary-1);}'}
`;
const StyledPrimaryTransparentButton = styled(StyledButton)<IHyperLinkProps>`
  ${basePrimaryTransparentButton};

  ${(props) =>
    props.disabled &&
    'opacity: 0.5; cursor: not-allowed; &:hover {background-color:  var(--black); color: var(--clr-primary-1);}'}
`;

const StyledTertiaryButton = styled(StyledButton)<IHyperLinkProps>`
  ${baseTertiaryButton};

  ${(props) =>
    props.disabled &&
    'opacity: 0.5; cursor: not-allowed; &:hover {background-color:  var(--clr-primary-2);}'}
`;

const StyledTransparentButton = styled(StyledButton)<IHyperLinkProps>`
  ${baseTransparentButton};

  ${(props) => props.disabled && 'opacity: 0.5; cursor: not-allowed;'}
`;

const NoStyledButton = styled(StyledButton)<IHyperLinkProps>``;

const StyledLinks: IHyperLinks = {
  'button-primary': StyledPrimaryButton,
  'button-primary-span': StyledPrimaryButtonSpan,
  'button-primary-transparent': StyledPrimaryTransparentButton,
  'button-secondary': StyledSecondaryButton,
  'button-secondary-span': StyledSecondaryButtonSpan,
  'button-tertiary': StyledTertiaryButton,
  'button-transparent': StyledTransparentButton,
  'no-style': NoStyledButton,
  link: StyledHyperlink,
};

/**
 * @deprecated Use css utils .tw-lf-link or .tw-lf-button instead
 */
const HyperLink = React.forwardRef<
  HTMLAnchorElement,
  PropsWithChildren<IHyperLinkProps>
>(
  (
    {
      children,
      disabled = false,
      onClick,
      className,
      variant = 'link',
      hasMinWidth = false,
      size = 'medium',
      href,
      to,
      dataTestId,
    },
    ref,
  ) => {
    const StyledLink = StyledLinks[variant];

    return (
      <StyledLink
        disabled={disabled}
        href={href || to}
        ref={ref}
        onClick={onClick}
        hasMinWidth={hasMinWidth}
        className={className}
        size={size}
        data-testid={dataTestId}
      >
        {children}
      </StyledLink>
    );
  },
);

export default HyperLink;
